import { invariant } from '@mimir/common/util/invariant';
import type { SimpleDialogRequest } from './simpleDialogRequest';
import type { BaseDialogProps } from './types';
import { showSimpleDialogFromRequest } from './simple';

/**
 * Show a simple dialog with a confirm and cancel button, the text can be configured
 * through dialogProps. The provided promise will be awaited, causing the dialog action button to spin until resolved.
 * It will close upon resolving.
 *
 * @param dialogProps The props to configure the dialog
 * @param actionFn The promise to await when the user confirms the dialog, if any.
 * @param secondaryFn Optionally provide a function to call when the user presses the secondary action button
 * @param bootstrappingFn Optionally provide a promise that will be awaited before the dialog contents are shown. The dialog
 *                        will show a loading spinner until the promise resolves.
 */
export async function showSimpleAsyncDialog(
  dialogProps: BaseDialogProps,
  actionFn: () => Promise<void>,
  secondaryActionFn?: () => Promise<void>,
  bootstrappingFn?: () => Promise<any>,
  cancelFn?: () => void,
) {
  const req: SimpleDialogRequest = {
    bootstrapping: bootstrappingFn !== undefined,
    loading: false,
    showDialog: true,
    dialogProps,
    callback: (response) => {
      async function run() {
        if (response === 'ok') {
          invariant(actionFn, 'Expected function to call on ok click');
          req.loading = true;
          await actionFn();
          req.loading = false;
          req.showDialog = false;
        } else if (response === 'secondaryOk') {
          invariant(secondaryActionFn, 'Expected function to call on secondary action');
          req.loading = true;
          await secondaryActionFn();
          req.loading = false;
          req.showDialog = false;
        } else if (response === 'cancel') {
          cancelFn?.();
          req.showDialog = false;
        }
      }
      run().catch(ex => console.error('showSimpleAsyncDialog callback', ex));
    }
  };
  showSimpleDialogFromRequest(req);
  if (bootstrappingFn) {
    await bootstrappingFn();
    req.bootstrapping = false;
  }
}
